import { useEffect, ReactElement } from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQuery, useApolloClient, gql, NetworkStatus } from '@apollo/client'
import format from 'date-fns/format'

import Heading from 'components/v2/Heading'
import { ClickRateIcon } from '@10xLabs/react-web/components/Icons/ClickRate'
import { ResponseRateIcon } from '@10xLabs/react-web/components/Icons/ResponseRate'
import { FormattedPercent } from '@10xLabs/react-web/components/FormattedPercent'
import { FormattedNumber } from '@10xLabs/react-web/components/FormattedNumber'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { getStatusColor } from 'features/Bookings/components/Status'
import { statusMessages } from 'features/Bookings/components/Status/messages'
import { resolvers as bookingsResolvers, updateBookingsDashboardWithSubscription } from 'resolvers/bookings'
import { device } from 'utils/style'
import { round } from 'utils/math'
import { DATE_FORMAT, getTimezone } from 'utils/date'
import { useLogin } from 'features/Login/hooks'
import { GTBookingV2Status } from 'gql/types.generated'

const COLOR = {
  BLUE: 'blue',
  GREEN: 'green',
  PURPLE: 'purple'
}

const Container = styled.div<TSFixMe>`
  padding: 30px 20px 15px;
  background-color: #1976d2;
  background-image: linear-gradient(168deg, #7130ff -12%, #1976d2 73%);
  color: #ffffff;
  overflow: auto;
`

const Title = styled.div`
  margin-bottom: 25px;

  ${Heading.H3} {
    color: #ffffff;
  }
`

const IndicatorsWrapper = styled.div<TSFixMe>`
  @media ${device.laptop} {
    display: flex;
    justify-content: space-between;
  }
`

const Indicators = styled.div<TSFixMe>`
  display: flex;

  > div {
    flex: 1 0 auto;
  }

  @media ${device.laptop} {
    height: auto;
    flex: 1 0 auto;
    justify-content: space-around;

    &:first-child {
      justify-content: flex-start;
    }
  }
`

const SplitIndicators = styled.div<TSFixMe>`
  height: 1px;
  min-height: 1px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.3);
  margin: 10px 0;

  @media ${device.laptop} {
    height: auto;
    width: 1px;
    min-width: 1px;
    margin: 0 10px;
  }
`

const Indicator = styled.div<TSFixMe>`
  > div {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const SpaceAround = styled.div<TSFixMe>`
  display: flex;
  width: 100%;

  > div {
    flex: 1 0 auto;
  }

  @media ${device.laptop} {
    justify-content: space-around;

    > div {
      flex: unset;
    }
  }
`

const IndicatorLabel = styled.div<TSFixMe>`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
`

const IndicatorValue = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
  font-size: 32px;
  color: ${(props): TSFixMe => getColor(props.color)};
`

const IndicatorIcon = styled.div<{ color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: ${({ color }): string => getColor(color) || 'initial'};
  margin-right: 15px;
`

const SalesChannel = styled.div<TSFixMe>`
  display: flex;
  flex-wrap: wrap;
  margin: -5px;
`

const SaleChannel = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
  margin: 5px;
`

const SaleChannelLabel = styled.div<TSFixMe>`
  font-size: 12px;
  text-transform: uppercase;
  margin-right: 5px;
`

const SaleChannelValue = styled.div<TSFixMe>`
  font-size: 16px;
`

const StatusTable = styled.table<TSFixMe>`
  border-collapse: collapse;

  tr:first-child {
    td {
      padding-top: 0px;
    }
  }

  td {
    padding: 5px;
    vertical-align: middle;

    &:first-child {
      padding-left: 0;
    }
  }
`

const StatusIcon = styled.div<TSFixMe>`
  width: 8px;
  height: 8px;
  border-color: ${(props): TSFixMe => getStatusColor(props.status)};
  border-width: 2px;
  border-style: solid;
  border-radius: 50%;
  margin-right: 5px;
`

const Flex = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
`

const BOOKINGS_DASHBOARD_RESPONSE = `
  day {
    confirmed {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    partiallyConfirmed {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    reserved {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    failed {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    cancelled {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    total @client
    onlinePercent @client
    offlinePercent @client
  }
  month {
    confirmed {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    partiallyConfirmed {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    reserved {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    failed {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    cancelled {
      online {
        count
        amount
      }
      offline {
        count
        amount
      }
      total @client
    }
    total @client
    onlinePercent @client
    offlinePercent @client
  }
`

const GET_BOOKINGS_DASHBOARD = gql`
  query GetBookingsDashboard($date: String!, $timezone: String!) {
    bookingsDashboard(input: {
      date: $date,
      timezone: $timezone
    }) {
      ${BOOKINGS_DASHBOARD_RESPONSE}
    }
  }
`

const BOOKINGS_DASHBOARD_SUBSCRIPTION = gql`
  subscription BookingsDashboardSubscription($date: String!, $timezone: String!) {
    bookingsDashboard(input: { date: $date, timezone: $timezone }) {
      bookingID
      status
      amount
      previous {
        status
        amount
      }
      createdAt
      saleChannel
    }
  }
`

const getColor = (color: string): string | undefined => {
  switch (color) {
    case COLOR.BLUE:
      return '#26c8f0'
    case COLOR.GREEN:
      return '#20e0a5'
    case COLOR.PURPLE:
      return '#ff5ff7'
  }
}

const FormattedPrice = ({ value }: { value: number }): ReactElement => {
  return <FormattedNumber value={round(value / 100)} prefix="$" notation="compact" />
}

let unsubscribe: TSFixMe

export const BookingsDashboard = (): TSFixMe => {
  let content
  const intl = useIntl()
  const client = useApolloClient()
  // @ts-expect-error ts(2322) FIXME
  client.addResolvers(bookingsResolvers)

  const login = useLogin()
  const { data, error, loading, refetch, networkStatus, subscribeToMore } = useQuery(GET_BOOKINGS_DASHBOARD, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      date: format(new Date(), DATE_FORMAT),
      timezone: getTimezone()
    }
  })

  useEffect((): TSFixMe => {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = undefined
    }

    return (): TSFixMe => {
      if (unsubscribe) {
        unsubscribe()
        unsubscribe = undefined
      }
    }
  }, [])

  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader white />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="bookings.dashboard.error.description"
            defaultMessage="There was an error while loading your dashboard"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="bookings.dashboard.error.action" defaultMessage="Reload your dashboard" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    if (!unsubscribe) {
      unsubscribe = subscribeToMore({
        document: BOOKINGS_DASHBOARD_SUBSCRIPTION,
        variables: {
          date: format(new Date(), DATE_FORMAT),
          timezone: getTimezone(),
          accessToken: `Bearer ${login.accessToken.value}`
        },
        updateQuery: updateBookingsDashboardWithSubscription
      })
    }

    const { day, month } = data.bookingsDashboard

    content = (
      <IndicatorsWrapper>
        <Indicators>
          <Indicator>
            <IndicatorLabel>
              <FormattedMessage id="bookings.dashboard.dailyBookings" defaultMessage="Daily bookings" />
            </IndicatorLabel>

            <IndicatorValue color={COLOR.BLUE}>
              <IndicatorIcon color={COLOR.BLUE}>
                <ClickRateIcon />
              </IndicatorIcon>

              <div>
                <FormattedNumber value={day.total.online.count + day.total.offline.count} />
              </div>
            </IndicatorValue>

            <SalesChannel>
              <SaleChannel>
                <SaleChannelLabel>
                  <FormattedMessage id="dictionary.offline" defaultMessage="Offline" />
                </SaleChannelLabel>

                <SaleChannelValue>
                  <FormattedPercent value={round(day.offlinePercent * 100)} />
                </SaleChannelValue>
              </SaleChannel>

              <SaleChannel>
                <SaleChannelLabel>
                  <FormattedMessage id="dictionary.online" defaultMessage="Online" />
                </SaleChannelLabel>

                <SaleChannelValue>
                  <FormattedPercent value={round(day.onlinePercent * 100)} />
                </SaleChannelValue>
              </SaleChannel>
            </SalesChannel>
          </Indicator>

          <Indicator>
            <IndicatorLabel>
              <FormattedMessage id="bookings.dashboard.dailySales" defaultMessage="Daily sales" />
            </IndicatorLabel>

            <IndicatorValue color={COLOR.GREEN}>
              <IndicatorIcon color={COLOR.GREEN}>
                <ResponseRateIcon />
              </IndicatorIcon>

              <div>
                <FormattedPrice value={day.total.online.amount + day.total.offline.amount} />
              </div>
            </IndicatorValue>
          </Indicator>
        </Indicators>

        <SplitIndicators />

        <Indicators>
          <SpaceAround>
            <Indicator>
              <IndicatorLabel>
                <FormattedMessage id="bookings.dashboard.monthBookings" defaultMessage="Month bookings" />
              </IndicatorLabel>

              <IndicatorValue color={COLOR.PURPLE}>
                <IndicatorIcon color={COLOR.PURPLE}>
                  <ClickRateIcon />
                </IndicatorIcon>

                <div>
                  <FormattedNumber value={month.total.online.count + month.total.offline.count} />
                </div>
              </IndicatorValue>

              <SalesChannel>
                <SaleChannel>
                  <SaleChannelLabel>
                    <FormattedMessage id="dictionary.offline" defaultMessage="Offline" />
                  </SaleChannelLabel>

                  <SaleChannelValue>
                    <FormattedPercent value={round(month.offlinePercent * 100)} />
                  </SaleChannelValue>
                </SaleChannel>

                <SaleChannel>
                  <SaleChannelLabel>
                    <FormattedMessage id="dictionary.online" defaultMessage="Online" />
                  </SaleChannelLabel>

                  <SaleChannelValue>
                    <FormattedPercent value={round(month.onlinePercent * 100)} />
                  </SaleChannelValue>
                </SaleChannel>
              </SalesChannel>
            </Indicator>

            <Indicator>
              <IndicatorLabel>
                <FormattedMessage id="bookings.dashboard.monthGains" defaultMessage="Month gains" />
              </IndicatorLabel>

              <IndicatorValue color={COLOR.GREEN}>
                <IndicatorIcon color={COLOR.GREEN}>
                  <ResponseRateIcon />
                </IndicatorIcon>

                <div>
                  <FormattedPrice value={month.total.online.amount + month.total.offline.amount} />
                </div>
              </IndicatorValue>
            </Indicator>
          </SpaceAround>
        </Indicators>

        <SplitIndicators />

        <Indicators>
          <SpaceAround>
            <Indicator>
              <IndicatorLabel>
                <FormattedMessage id="bookings.dashboard.overview" defaultMessage="Overview" />
              </IndicatorLabel>

              <div>
                <StatusTable>
                  <tbody>
                    {month.confirmed.total.count > 0 && (
                      <tr>
                        <td>
                          <Flex>
                            <StatusIcon status={GTBookingV2Status.Confirmed} />
                            {intl.formatMessage(statusMessages[GTBookingV2Status.Confirmed])}
                          </Flex>
                        </td>
                        <td>
                          <FormattedNumber value={month.confirmed.total.count} />
                        </td>
                        <td>
                          <FormattedNumber value={round(month.confirmed.total.amount / 100, 2)} prefix="$" />
                        </td>
                      </tr>
                    )}
                    {month.partiallyConfirmed.total.count > 0 && (
                      <tr>
                        <td>
                          <Flex>
                            <StatusIcon status={GTBookingV2Status.Confirmed} />
                            {intl.formatMessage(statusMessages[GTBookingV2Status.PartiallyConfirmed])}
                          </Flex>
                        </td>
                        <td>
                          <FormattedNumber value={month.partiallyConfirmed.total.count} />
                        </td>
                        <td>
                          <FormattedNumber value={round(month.partiallyConfirmed.total.amount / 100, 2)} prefix="$" />
                        </td>
                      </tr>
                    )}
                    {month.reserved.total.count > 0 && (
                      <tr>
                        <td>
                          <Flex>
                            <StatusIcon status={GTBookingV2Status.Reserved} />
                            {intl.formatMessage(statusMessages[GTBookingV2Status.Reserved])}
                          </Flex>
                        </td>
                        <td>
                          <FormattedNumber value={month.reserved.total.count} />
                        </td>
                        <td>
                          <FormattedNumber value={round(month.reserved.total.amount / 100, 2)} prefix="$" />
                        </td>
                      </tr>
                    )}
                    {month.failed.total.count > 0 && (
                      <tr>
                        <td>
                          <Flex>
                            <StatusIcon status={GTBookingV2Status.Failed} />
                            {intl.formatMessage(statusMessages[GTBookingV2Status.Failed])}
                          </Flex>
                        </td>
                        <td>
                          <FormattedNumber value={month.failed.total.count} />
                        </td>
                        <td>
                          <FormattedNumber value={round(month.failed.total.amount / 100, 2)} prefix="$" />
                        </td>
                      </tr>
                    )}
                    {month.cancelled.total.count > 0 && (
                      <tr>
                        <td>
                          <Flex>
                            <StatusIcon status={GTBookingV2Status.Cancelled} />
                            {intl.formatMessage(statusMessages[GTBookingV2Status.Cancelled])}
                          </Flex>
                        </td>
                        <td>
                          <FormattedNumber value={month.cancelled.total.count} />
                        </td>
                        <td>
                          <FormattedNumber value={round(month.cancelled.total.amount / 100, 2)} prefix="$" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </StatusTable>
              </div>
            </Indicator>
          </SpaceAround>
        </Indicators>
      </IndicatorsWrapper>
    )
  }

  return (
    <Container>
      <Title>
        <Heading.H3>
          <FormattedMessage id="bookings.header.label" defaultMessage="Bookings" />
        </Heading.H3>
      </Title>
      {content}
    </Container>
  )
}
